import React, { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, Eye, EyeOff, Edit2, Globe, Lock, Package, Tag, QrCode, Barcode,
  MapPin, Truck, Upload, Link, Check,
} from 'lucide-react';
import { LabelElement } from '@/types/label';
import type { Json } from '@/integrations/supabase/types';
import { TemplateSubmissions } from '@/components/admin/TemplateSubmissions';

interface TemplateItem {
  id: string; name: string; isGlobal: boolean; userId: string; username?: string;
  width: number; height: number; elementCount: number; updatedAt: string;
}

interface PresetItem {
  id: string; name: string; description: string; icon: string;
  elements: LabelElement[]; isActive: boolean; sortOrder: number;
  linkUrl: string | null; urlFields: string[] | null;
}

interface TemplateManagementProps {
  templates: TemplateItem[];
  presets: PresetItem[];
  templatesLoading: boolean;
  presetsLoading: boolean;
  onRefreshTemplates: () => void;
  onRefreshPresets: () => void;
}

export const TemplateManagement: React.FC<TemplateManagementProps> = ({
  templates, presets, templatesLoading, presetsLoading,
  onRefreshTemplates, onRefreshPresets,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editPresetOpen, setEditPresetOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<PresetItem | null>(null);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [presetIcon, setPresetIcon] = useState('package');
  const [presetIsActive, setPresetIsActive] = useState(true);
  const [presetLinkUrl, setPresetLinkUrl] = useState('');
  const [presetUrlFields, setPresetUrlFields] = useState<string[]>([]);
  const [isCreatingPreset, setIsCreatingPreset] = useState(false);

  const handleToggleGlobal = async (id: string, isGlobal: boolean) => {
    const { error } = await supabase.from('templates').update({ is_global: isGlobal }).eq('id', id);
    if (error) { toast.error('Failed'); return; }
    toast.success(isGlobal ? 'Now global' : 'Now private');
    onRefreshTemplates();
  };

  const handleDeleteTemplate = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    const { error } = await supabase.from('templates').delete().eq('id', id);
    if (error) { toast.error('Failed'); return; }
    toast.success('Deleted');
    onRefreshTemplates();
  };

  const handleTogglePresetActive = async (id: string, active: boolean) => {
    const { error } = await supabase.from('template_presets').update({ is_active: active }).eq('id', id);
    if (error) { toast.error('Failed'); return; }
    toast.success(active ? 'Visible' : 'Hidden');
    onRefreshPresets();
  };

  const getPresetFields = (elements: LabelElement[]): string[] => {
    const fields = new Set<string>();
    elements.forEach(el => {
      const content = (el as any).content || '';
      const matches = content.matchAll(/\{\{(\w+)\}\}/g);
      for (const match of matches) fields.add(match[1]);
    });
    return [...fields];
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) { toast.error('Enter a name'); return; }
    setIsCreatingPreset(true);
    try {
      if (selectedPreset) {
        const { error } = await supabase.from('template_presets').update({
          name: presetName.trim(), description: presetDescription.trim(), icon: presetIcon,
          is_active: presetIsActive, link_url: presetLinkUrl.trim() || null,
          url_fields: presetUrlFields.length > 0 ? presetUrlFields : null,
        } as any).eq('id', selectedPreset.id);
        if (error) throw error;
        toast.success('Updated');
      } else {
        const maxOrder = Math.max(0, ...presets.map(p => p.sortOrder));
        const { error } = await supabase.from('template_presets').insert({
          name: presetName.trim(), description: presetDescription.trim(), icon: presetIcon,
          is_active: presetIsActive, sort_order: maxOrder + 1, elements: [] as unknown as Json,
        });
        if (error) throw error;
        toast.success('Created');
      }
      setEditPresetOpen(false);
      onRefreshPresets();
    } catch { toast.error('Failed'); }
    finally { setIsCreatingPreset(false); }
  };

  const handleDeletePreset = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    const { error } = await supabase.from('template_presets').delete().eq('id', id);
    if (error) { toast.error('Failed'); return; }
    toast.success('Deleted');
    onRefreshPresets();
  };

  const handleImportToPreset = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const template = JSON.parse(text);
      if (!template.name || !Array.isArray(template.elements)) throw new Error('Invalid');
      const maxOrder = Math.max(0, ...presets.map(p => p.sortOrder));
      const { error } = await supabase.from('template_presets').insert({
        name: template.name, description: `Imported from ${file.name}`, icon: 'package',
        is_active: true, sort_order: maxOrder + 1, elements: template.elements as unknown as Json,
      });
      if (error) throw error;
      toast.success(`Preset "${template.name}" created`);
      onRefreshPresets();
    } catch { toast.error('Failed to import'); }
    e.target.value = '';
  };

  const openEditPreset = (preset: PresetItem) => {
    setSelectedPreset(preset); setPresetName(preset.name); setPresetDescription(preset.description);
    setPresetIcon(preset.icon); setPresetIsActive(preset.isActive);
    setPresetLinkUrl(preset.linkUrl || ''); setPresetUrlFields(preset.urlFields || getPresetFields(preset.elements));
    setEditPresetOpen(true);
  };

  const openNewPreset = () => {
    setSelectedPreset(null); setPresetName(''); setPresetDescription('');
    setPresetIcon('package'); setPresetIsActive(true); setPresetLinkUrl(''); setPresetUrlFields([]);
    setEditPresetOpen(true);
  };

  const copyPresetUrl = (preset: PresetItem) => {
    const allFields = getPresetFields(preset.elements);
    const selected = preset.urlFields ?? allFields;
    const params = new URLSearchParams();
    params.set('preset', preset.name);
    selected.forEach(f => params.set(f, `{${f}}`));
    navigator.clipboard.writeText(`${window.location.origin}/?${decodeURIComponent(params.toString())}`);
    toast.success('URL copied');
  };

  const getPresetIcon = (iconName: string) => {
    const icons: Record<string, React.ReactNode> = {
      'package': <Package className="w-4 h-4" />, 'tag': <Tag className="w-4 h-4" />,
      'qr-code': <QrCode className="w-4 h-4" />, 'barcode': <Barcode className="w-4 h-4" />,
      'file-text': <MapPin className="w-4 h-4" />, 'truck': <Truck className="w-4 h-4" />,
    };
    return icons[iconName] || <Package className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Templates</h2>
        <p className="text-sm text-muted-foreground">Manage templates, presets, and submissions</p>
      </div>

      <TemplateSubmissions />

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe className="w-4 h-4 text-primary" />All Templates
            </CardTitle>
          </CardHeader>
          <CardContent>
            {templatesLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : templates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No templates</div>
            ) : (
              <ScrollArea className="h-[450px]">
                <div className="space-y-2">
                  {templates.map(t => (
                    <div key={t.id} className="flex items-center justify-between p-2.5 rounded-lg border hover:bg-muted/30">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {t.isGlobal ? <Globe className="w-3.5 h-3.5 text-primary shrink-0" /> : <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                          <span className="font-medium text-sm truncate">{t.name}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5 ml-5">
                          {t.username} • {t.elementCount} el • {t.width}×{t.height}mm
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <Button variant={t.isGlobal ? 'secondary' : 'outline'} size="sm" className="h-7 text-xs gap-1" onClick={() => handleToggleGlobal(t.id, !t.isGlobal)}>
                          {t.isGlobal ? <><Globe className="w-3 h-3" />Global</> : <><Lock className="w-3 h-3" />Private</>}
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteTemplate(t.id, t.name)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Presets */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Package className="w-4 h-4 text-primary" />Presets
              </CardTitle>
              <div className="flex gap-1.5">
                <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={handleImportToPreset} />
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="w-3 h-3" />Import
                </Button>
                <Button size="sm" className="h-7 text-xs gap-1" onClick={openNewPreset}>
                  <Plus className="w-3 h-3" />New
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {presetsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : presets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No presets</div>
            ) : (
              <ScrollArea className="h-[450px]">
                <div className="space-y-2">
                  {presets.map(preset => (
                    <div key={preset.id} className={`p-3 rounded-lg border ${preset.isActive ? 'hover:bg-muted/30' : 'opacity-60'}`}>
                      <div className="flex items-start gap-3">
                        <div className="p-1.5 rounded-md bg-primary/10 text-primary shrink-0">{getPresetIcon(preset.icon)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{preset.name}</span>
                            {!preset.isActive && <Badge variant="secondary" className="text-[10px]"><EyeOff className="w-3 h-3 mr-0.5" />Hidden</Badge>}
                          </div>
                          <p className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5">{preset.description}</p>
                          <p className="text-[10px] text-muted-foreground">{preset.elements.length} elements</p>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleTogglePresetActive(preset.id, !preset.isActive)}>
                            {preset.isActive ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyPresetUrl(preset)}><Link className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditPreset(preset)}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeletePreset(preset.id, preset.name)}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Preset Dialog */}
      <Dialog open={editPresetOpen} onOpenChange={setEditPresetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedPreset ? 'Edit Preset' : 'Create Preset'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Name</Label><Input value={presetName} onChange={(e) => setPresetName(e.target.value)} /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={presetDescription} onChange={(e) => setPresetDescription(e.target.value)} /></div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select value={presetIcon} onValueChange={setPresetIcon}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[['package','Package'], ['tag','Tag'], ['qr-code','QR Code'], ['barcode','Barcode'], ['file-text','Address'], ['truck','Shipping']].map(([v,l]) => (
                    <SelectItem key={v} value={v}><div className="flex items-center gap-2">{getPresetIcon(v)}{l}</div></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Link URL Pattern</Label>
              <Input placeholder="https://example.com/{{field}}" value={presetLinkUrl} onChange={(e) => setPresetLinkUrl(e.target.value)} />
            </div>
            <div className="flex items-center space-x-2">
              <Switch checked={presetIsActive} onCheckedChange={setPresetIsActive} />
              <Label>Visible to users</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPresetOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePreset} disabled={isCreatingPreset || !presetName.trim()}>
              {isCreatingPreset ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {selectedPreset ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
